import React from 'react';
import { useTranslation } from 'react-i18next';

import { TranslationFunctionType } from '@/shared/globalTypes';
import { Box, TextCopy } from '@/shared/ui';
import { DialogAlert } from '@/shared/ui/DialogAlert';
import { QrCode } from '@/shared/ui/QrCode';
import { useClipboard } from '@/shared/ui/TextCopy/useClipboard';
import { Button } from '@/shared/ui/lib/Button';

import { useWhatsappPasskey } from '../../model/useWhatsappPasskey';
import { ChannelStepsList } from '../scanQrCode/ChannelStepsList';
import { ContentAndQr } from '../scanQrCode/ContentAndQr';
import { WarningTextAboutDialogs } from '../scanQrCode/WarningTextAboutDialogs';

interface ChannelQrCodeContentProps {
  qr?: string;
  id: string;
}

const getSteps = (t: TranslationFunctionType) => [
  t('channels:whatsAppPersonal.stepsList.step1'),
  t('channels:whatsAppPersonal.stepsList.step2'),
  t('channels:whatsAppPersonal.stepsList.step3'),
];

const PASSKEY_STEP_TEXT_KEYS: Record<string, string> = {
  extension_missing: 'channels:whatsAppPersonal.passkeyStep.extensionMissing',
  ready_to_sign: 'channels:whatsAppPersonal.passkeyStep.ready',
  signing: 'channels:whatsAppPersonal.passkeyStep.signing',
  submitted: 'channels:whatsAppPersonal.passkeyStep.submitted',
  error: 'channels:whatsAppPersonal.passkeyStep.error',
};

export const DialogContent: React.FC<ChannelQrCodeContentProps> = ({
  qr,
  id,
}) => {
  const { t } = useTranslation();
  const { copyToClipboard } = useClipboard();
  const passkey = useWhatsappPasskey(id, true);

  const handleCopyLink = async () => {
    const link = `${window.location.origin}/qr/${id}`;
    await copyToClipboard(link);
  };

  const passkeyVisible =
    passkey.phase === 'extension_missing' ||
    passkey.phase === 'ready_to_sign' ||
    passkey.phase === 'signing' ||
    passkey.phase === 'submitted' ||
    passkey.phase === 'error';

  return (
    <Box sx={{ maxWidth: '670px' }}>
      <DialogAlert sx={{ mb: '2px' }}>
        {t('channels:whatsAppPersonal.recommendation')}
      </DialogAlert>
      <DialogAlert sx={{ mb: '2px' }}>
        {t('channels:whatsAppPersonal.recommendationOpenApp')}
      </DialogAlert>
      <DialogAlert sx={{ mb: '25px' }}>
        <strong>{t('channels:whatsAppPersonal.passkeyNoticeTitle')}</strong>
        <br />
        {t('channels:whatsAppPersonal.passkeyNoticeBody')}
      </DialogAlert>
      {passkeyVisible && (
        <DialogAlert sx={{ mb: '25px' }}>
          <strong>{t('channels:whatsAppPersonal.passkeyStep.title')}</strong>
          <br />
          {t(PASSKEY_STEP_TEXT_KEYS[passkey.phase] ?? 'channels:whatsAppPersonal.passkeyStep.error')}
          {passkey.phase === 'ready_to_sign' && (
            <Button variant="contained" onClick={() => void passkey.confirm()} sx={{ mt: '12px' }}>
              {t('channels:whatsAppPersonal.passkeyStep.button')}
            </Button>
          )}
        </DialogAlert>
      )}
      <ContentAndQr
        contentSlot={<ChannelStepsList steps={getSteps(t)} />}
        underContentSlot={<WarningTextAboutDialogs />}
        qrSlot={<QrCode qrData={qr} />}
        underQrSlot={
          <TextCopy
            onClick={handleCopyLink}
            sx={{ cursor: 'pointer', textAlign: 'center' }}
            color="primary"
            label={t('channels:copyQrCode')}
          />
        }
      />
    </Box>
  );
};
